import { StackScreenProps } from "@react-navigation/stack";
import { AddGiftCardProductCartRootParamList } from "../../../../types";
import {
  Page,
  View,
  Text,
  Stack,
  Button,
  TextField,
  Label,
  Divider,
  useFlashMessages,
} from "@towersystems/roam-common-native";
import { moneyToFormattedString } from "@towersystems/roam-common/lib/shared-utils";
import { isValidMoneyValue } from "../../../../utilities/is-valid-money";
import React from "react";
import { useTranslation } from "react-i18next";
import { useAddItem } from "../../utils/sale-controller";
import { useAddGiftCardProductScreenProvider } from "./hooks";
import { Feather } from "@expo/vector-icons";
import { Colors } from "react-native-ui-lib";

export interface AddGiftCardProductScreenProps
  extends StackScreenProps<
    AddGiftCardProductCartRootParamList,
    "AddGiftCardProductScreen"
  > {}

export const AddGiftCardProductScreen = ({
  navigation,
  route,
}: AddGiftCardProductScreenProps) => {
  const { t } = useTranslation("gift_card");
  const { giftCode } = route.params;
  const flashMessagesContext = useFlashMessages();

  const addItem = useAddItem({ navigation: navigation });
  const { productId } = useAddGiftCardProductScreenProvider();

  const [cardAmount, setCardAmount] = React.useState<string | undefined>(
    undefined
  );

  const isValidAmount = React.useMemo(() => {
    if (!cardAmount) {
      return false;
    }
    if (!isValidMoneyValue(cardAmount)) {
      return false;
    }

    // check if is positive number and not zero
    if (parseFloat(cardAmount) <= 0) {
      return false;
    }

    return true;
  }, [cardAmount]);

  const handleOnSetCardAmount = React.useCallback(
    (amount: string) => {
      setCardAmount(amount);
    },
    [setCardAmount]
  );

  const isInvalid =
    !giftCode || !giftCode.code || giftCode.canBeRedeemed == true;

  const giftCardNumberMarkup = (
    <Stack centerVertical>
      <View>
        <Text>Gift card number</Text>
        <Text variant="heading">{giftCode.code}</Text>
      </View>
    </Stack>
  );

  const giftCardBalanceMarkup = isInvalid == false && (
    <Stack centerVertical>
      <View>
        <Text>Balance</Text>
        <Text variant="heading">
          {moneyToFormattedString(giftCode.balance)}
        </Text>
      </View>
    </Stack>
  );

  const expiryDateMarkup = (
    <Stack centerVertical>
      <View>
        <Text>Expiry date</Text>
        <Text variant="heading">{giftCode.expiryDate}</Text>
      </View>
    </Stack>
  );

  const handleOnAddToSale = React.useCallback(
    async (productId: string, giftCardCode: string, amount: number) => {
      try {
        await addItem(productId, {
          giftCardNumber: giftCardCode,
          basePrice: amount,
        });
        navigation.getParent()?.goBack();
      } catch (err) {
        flashMessagesContext.showMessage({
          message: "Failed to add gift card to sale",
          severity: "error",
        });
      }
    },
    [cardAmount]
  );

  if (isInvalid) {
    return (
      <Page
        title={t("title.invalid_gift_card_code")}
        secondaryTitle={t("title.invalid_gift_card_code_secondary")}
        footer={
          <Button
            onPress={() => navigation.goBack()}
            label="Enter a new code"
            isAction
            icon={<Feather name="arrow-left" size={24} color={Colors.action} />}
          ></Button>
        }
      >
        <Stack vertical>
          {giftCardNumberMarkup}
          {expiryDateMarkup}
          {giftCardBalanceMarkup}
        </Stack>
      </Page>
    );
  }

  const inputMarkup = (
    <View>
      <Label label={t("form.gift_card_value.label")} />
      <TextField
        placeholder={t("form.gift_card_value.placeholder")}
        onChangeText={handleOnSetCardAmount}
        value={cardAmount}
        formatterType={isValidAmount ? "money" : undefined}
        validationMessage={
          cardAmount && !isValidAmount ? "Invalid amount" : undefined
        }
      />
    </View>
  );

  const buttonMarkup = (
    <Button
      label="Add to sale"
      disabled={!isValidAmount}
      onPress={() => {
        if (cardAmount && isValidMoneyValue(cardAmount)) {
          handleOnAddToSale(productId, giftCode.code, parseFloat(cardAmount));
        }
      }}
      fullWidth
      isAction
    />
  );

  return (
    <Page
      title={t("title.add_sale_gift_card")}
      secondaryTitle={t("title.add_sale_gift_card_secondary")}
      footer={buttonMarkup}
    >
      <Stack vertical>
        {giftCardNumberMarkup}
        <Divider />
        {inputMarkup}
      </Stack>
    </Page>
  );
};
